// Libraries
import {Map, List, RecordOf} from 'immutable';
import {get} from 'lodash';

// Constants
import {
  ACTION_ITEM_TARGET_TYPES,
  IX2ContinuousActionGroup,
  IX2ContinuousActionListAction,
  IX2TimedActionGroup,
} from '@packages/systems/ix2/shared-constants-IX2Actions';
import {
  IX2ActionItemTarget,
  IX2Interactions,
  type IX2ActionItemTargetData,
  type IX2ActionListType,
  IX2StoreStateType,
  InteractionsType,
  EventsType,
  ActionListsType,
  IX2ActionList,
  ActionItemType,
  ActionItemTargetType,
  InteractionType,
  EventType,
  ActionListType,
  IX2List,
  IX2EventActionType,
  IX2ConfigurationTypes,
  PARAMETER_TYPES,
  IX2StoreState,
} from '@packages/systems/ix2/types-core';
import {
  type InteractionRecordType,
  IX2StateRecord,
} from '@packages/systems/ix2/designer/records/immutable';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';
import {
  EVENT_AUTO_STOP_ID_KEY_PATH,
  EVENT_ACTIONLIST_ID_KEY_PATH,
} from '@packages/systems/ix2/shared-constants-IX2Events';

import {
  getNodeIdFromIx2Id,
  setNodeIdFromIx2Id,
  getUniqueEventId,
  getUniqueInteractionId,
  replaceNodeId,
  prependPageId,
  replacePageId,
  getUniqueActionListId,
  getUniqueActionItemId,
  getUniqueParameterGroupId,
  hasPageId,
} from '@packages/systems/ix2/shared-utils';

import {
  EVENTS,
  ACTION_LISTS,
  INTERACTIONS,
  INITIAL_SELECTED_NODE_IN_INSTANCE_NATIVE_ID_PATH,
  INITIAL_FOCUSED_SYMBOL_NATIVE_ID_PATH,
  InteractionPages,
} from '@packages/systems/ix2/designer/constants';

import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';

// Shared utils
import {maybe, None} from '@packages/utilities/fp/option';
import {weakMemo} from '@packages/utilities/memo';
import {
  createConflictResolver,
  getPrefix,
  slugify,
} from '@packages/utilities/string-utils';
import {find} from '@packages/systems/core/utils/ImmUtils';
import {
  map,
  compose,
  constantIdentity,
  pipe,
  blackbird,
  zip,
  reduce,
  emptyArray,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {has, assoc} from '@packages/utilities/key-map';

import {reifyIx2State} from '@packages/systems/ix2/designer/records/immutable';
import {
  flatten,
  isInsideUserComponentRoot,
  isUserComponentInst,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {isLottie, isSpline, isRive} from '@packages/utilities/elementTypeUtils';
import {
  selectRootElementWithFocusedInstancesExpanded,
  getLiveComponentMap,
} from '@packages/utilities/platform-selectors';
import {
  Expressions,
  Component,
  Plugins,
  ElementPreset,
} from '@packages/systems/wfdl';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getPluginName,
  PluginName,
  Qualified,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {
  ActionTypeConsts,
  EventContinuousMouseAxes,
} from '@packages/systems/ix2/shared-constants';
import {
  deduplicateIx2Data,
  getInteractionsFromComponentRender,
} from '@packages/systems/libraries';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  getActionItemsFromList,
  getSelectedActionItems,
  getSelectedInteraction,
} from '@packages/systems/ix2/designer/lib';

const ACTION_TARGET_HASH_PREFIX = 'ACTION_TARGET';
const ACTION_TARGET_HASH_DELIMITER = '::';

export const ACTION_TARGET_TYPES = {
  /**
   * Action item will affect the element that it is currently assigned to
   */

  ELEMENT: 'ELEMENT',

  /**
   * Action item will affect the classes that are assigned to the target element.
   */

  ELEMENT_CLASS: 'ELEMENT_CLASS',

  /**
   * Action item will affect the element that is triggering the interaction.
   */

  TRIGGER_ELEMENT: 'TRIGGER_ELEMENT',
} as const;

export const updateEachActionGroupsInList =
  (fn: any) => (actionList: IX2ActionListType) => {
    // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2ActionListType'.
    if (actionList.continuousParameterGroups != null) {
      // @ts-expect-error - TS2339 - Property 'update' does not exist on type 'IX2ActionListType'. | TS7006 - Parameter 'parameterGroups' implicitly has an 'any' type.
      return actionList.update('continuousParameterGroups', (parameterGroups) =>
        // @ts-expect-error - TS7006 - Parameter 'parameterGroup' implicitly has an 'any' type.
        parameterGroups.map((parameterGroup) =>
          parameterGroup.update('continuousActionGroups', fn)
        )
      );
    }

    // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'IX2ActionListType'.
    if (actionList.actionItemGroups != null) {
      // @ts-expect-error - TS2339 - Property 'update' does not exist on type 'IX2ActionListType'.
      return actionList.update('actionItemGroups', fn);
    }

    return actionList;
  };

export const updateEachActionGroupInList = compose(
  updateEachActionGroupsInList
)(map);

export const updateActionItemsInList = (fn: any) =>
  updateEachActionGroupInList((actionGroup) =>
    // @ts-expect-error - TS18046 - 'actionGroup' is of type 'unknown'.
    actionGroup.update('actionItems', fn)
  );

export const isIx2ForbiddenElement = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): boolean =>
  isUserComponentInst(componentMap, element) &&
  !Plugins.isPluginComponentInst(plugins, element);

const replaceActionItemsTargetsIds = (nodeIdPairs: any) => (actionItems: any) =>
  // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
  actionItems.map((item) =>
    // @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
    item.updateIn(['config', 'target'], (target) => {
      return target && target.nodeId
        ? castActionItemTarget(target).set(
            'nodeId',
            nodeIdPairs.get(target.nodeId, target.nodeId)
          )
        : target;
    })
  );

export const replaceAffectedTargetIds =
  (nodeIdPairs: any) => (affectedElements: any) =>
    // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type.
    affectedElements.mapKeys((key) => nodeIdPairs.get(key, key));

export const updateItemTargetIdsInList =
  (nodeIdPairs: any) => (actionList: any) =>
    updateActionItemsInList(replaceActionItemsTargetsIds(nodeIdPairs))(
      actionList
    );

const updateInteractionTargetIds = (fn: any) => (interaction: any) =>
  interaction.update('target', fn);

const AFFECTED_ELEMENTS_PATH = ['action', 'config', 'affectedElements'];
const TARGET_ID_PATH = ['target', 'id'];
const updateEventTargetIds = (updater: any) => (event: any) => {
  if (event.hasIn(AFFECTED_ELEMENTS_PATH)) {
    // @ts-expect-error - TS7006 - Parameter 'affected' implicitly has an 'any' type.
    event = event.updateIn(AFFECTED_ELEMENTS_PATH, (affected) =>
      affected.mapKeys(updater)
    );
  }

  if (event.has('targets')) {
    // @ts-expect-error - TS7006 - Parameter 'targets' implicitly has an 'any' type.
    event = event.update('targets', (targets) =>
      // @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
      targets.map((target) => target.update('id', updater))
    );
  }

  return event.updateIn(TARGET_ID_PATH, updater);
};

const CONFIG_TARGET_PATH = ['config', 'target'];
const updateActionListTargetIds = (fn: any) =>
  updateActionItemsInList(
    map((item) =>
      // @ts-expect-error - TS18046 - 'item' is of type 'unknown'. | TS7006 - Parameter 'target' implicitly has an 'any' type.
      item.updateIn(CONFIG_TARGET_PATH, (target) =>
        target && target.nodeId
          ? castActionItemTarget(target).update('nodeId', fn)
          : target
      )
    )
  );

export const updateTargetIds =
  (updater: any) => (ix2State: IX2StoreStateType) =>
    ix2State
      .update(INTERACTIONS, map(updateInteractionTargetIds(updater)))
      .update(EVENTS, map(updateEventTargetIds(updater)))
      .update(ACTION_LISTS, map(updateActionListTargetIds(updater)));

export const replaceTargetIds = (nodeIdPairs: any) =>
  // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
  updateTargetIds((id) => nodeIdPairs.get(id, id));

// @ts-expect-error - TS2322 - Type '(arg4: any) => (arg5: unknown[]) => Map<unknown, unknown>' is not assignable to type '(arg1: any) => (arg1: any) => Map<string, string>'.
export const nodeIdsToMap: (arg1: any) => (arg1: any) => Map<string, string> =
  blackbird(Map)(zip);

export const mapToIds = weakMemo(map((node: {id: string}) => node.id));

export const mapPageIdToNodeIds = (currentPageId: any) =>
  // @ts-expect-error - migrating fp packages to TypeScript
  map((nodeId) => prependPageId(currentPageId, nodeId));

export const toFlattenedIds = compose(mapToIds)(flatten);

const shouldReplaceIds = ({
  isAnchorComponent,
  isClippedComponent,
  position,
  anchorElement,
  componentMap,
}: {
  isAnchorComponent: boolean;
  isClippedComponent: boolean;
  position: 'before' | 'after';
  anchorElement: ExpressionElement;
  componentMap: Component.ComponentMap;
}): boolean => {
  if (
    (!isAnchorComponent && !isClippedComponent) ||
    (isAnchorComponent && isClippedComponent)
  ) {
    return true;
  }

  if (isAnchorComponent && !isClippedComponent) {
    if (position === 'before' || position === 'after') {
      return isUserComponentInst(componentMap, anchorElement);
    }
  }

  return false;
};

export const getElementMoveTargetIdsMap = weakMemo(
  (payload: any): null | Map<string, string> => {
    const {designerState, id, anchorId, position, uiNodeState} = payload;
    const currentPageId = designerState.get('currentPageId');
    const componentMap = getLiveComponentMap(designerState);

    const root = selectRootElementWithFocusedInstancesExpanded({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });

    if (!root) return null;

    const anchorElement = Expressions.getElementById(root, anchorId);

    const clippedElement = Expressions.getElementById(root, id);

    const isAnchorComponent = Boolean(
      anchorElement &&
      root &&
      isInsideUserComponentRoot(componentMap)(root)(anchorElement)
    );

    const isClippedComponent = Boolean(
      clippedElement &&
      root &&
      isInsideUserComponentRoot(componentMap)(root)(clippedElement)
    );

    if (!anchorElement) return null;

    if (
      shouldReplaceIds({
        isAnchorComponent,
        isClippedComponent,
        position,
        anchorElement,
        componentMap,
      })
    ) {
      return null;
    }

    const element =
      clippedElement && (isClippedComponent || isAnchorComponent)
        ? clippedElement
        : anchorElement;
    const nodeIds = toFlattenedIds(element);
    const prevIds = isAnchorComponent
      ? mapPageIdToNodeIds(currentPageId)(nodeIds)
      : nodeIds;
    const newIds = isClippedComponent
      ? mapPageIdToNodeIds(currentPageId)(nodeIds)
      : nodeIds;
    return nodeIdsToMap(prevIds)(newIds);
  }
);

export const updateActionItemTarget = (
  actionItem: any,
  element: ExpressionElement | null
) =>
  actionItem.setIn(
    ['config', 'target'],
    actionItem.config.target
      .update(
        'nodeId',
        // @ts-expect-error - TS7006 - Parameter 'nodeId' implicitly has an 'any' type.
        (nodeId) =>
          element
            ? replaceNodeId(element.id)(actionItem.config.target.nodeId)
            : nodeId
      )
      .set('styleBlockIds', List(element ? getStyleBlockIds(element) : []))
  );

export const getActionItemTargetNodeId = (
  actionItemOrTarget: any,
  // @ts-expect-error - TS2749 - 'IX2State' refers to a value, but is being used as a type here. Did you mean 'typeof IX2State'?
  ix2StateOrSelectedInteraction: IX2State | InteractionRecordType
) => {
  let target;
  if (
    typeof actionItemOrTarget.has === 'function' &&
    actionItemOrTarget.has('nodeId') &&
    actionItemOrTarget.has('appliesTo')
  ) {
    target = actionItemOrTarget;
  } else if (actionItemOrTarget.getIn) {
    target = actionItemOrTarget.getIn(['config', 'target']);
  } else {
    target = get(actionItemOrTarget, ['config', 'target']);
  }
  if (!target) {
    return null;
  }

  const appliesTo = target.appliesTo;

  // If appliesTo is the interaction trigger, use the selected interaction target instead of
  // the assigned target nodeId
  if (appliesTo === ACTION_ITEM_TARGET_TYPES.TRIGGER_ELEMENT) {
    const selectedInteraction =
      /* @ts-expect-error Part of IX2 swap work, added in
       * https://github.com/webflow/webflow/pull/37941/ */
      ix2StateOrSelectedInteraction.events != null
        ? /* @ts-expect-error Part of IX2 swap work, added in
           * https://github.com/webflow/webflow/pull/37941/ */
          getSelectedInteraction(ix2StateOrSelectedInteraction)
        : ix2StateOrSelectedInteraction;

    if (selectedInteraction) {
      return selectedInteraction.target;
    }
  }

  // Return the nodeId the action item is initially assigned to by default -- even for "interaction trigger"
  // action items.
  return target.nodeId;
};

export const getActionItemTargetElementId = (
  // @ts-expect-error - TS2749 - 'IX2ActionItemTarget' refers to a value, but is being used as a type here. Did you mean 'typeof IX2ActionItemTarget'?
  target: IX2ActionItemTarget,
  ix2StateOrInteraction: any
) => {
  if (!target) {
    return null;
  }

  // deserialize if is a stringified version of an action target.
  if (
    typeof target === 'string' &&
    target.indexOf(ACTION_TARGET_HASH_PREFIX) === 0
  ) {
    target = createActionTargetFromHash(target);
  }

  let targetNodeId;

  // Helper line for upgrading parts of the codebase that is not passing in the third param here
  if (ix2StateOrInteraction) {
    targetNodeId = getActionItemTargetNodeId(target, ix2StateOrInteraction);
  } else {
    targetNodeId = target.nodeId;
  }

  return getNodeIdFromIx2Id(targetNodeId);
};

export const getActionItemTargetElement = (
  target: RecordOf<IX2ActionItemTargetData>,
  componentRender: Expression | null,
  ix2StateOrInteraction: any
): ExpressionElement | null => {
  if (!componentRender) return null;
  const elementId = getActionItemTargetElementId(target, ix2StateOrInteraction);
  return elementId
    ? (Expressions.getElementById(componentRender, elementId) ?? null)
    : null;
};

export const castActionItemTarget = (
  // @ts-expect-error - TS2749 - 'IX2ActionItemTarget' refers to a value, but is being used as a type here. Did you mean 'typeof IX2ActionItemTarget'?
  targetConfig: IX2ActionItemTarget | IX2ActionItemTargetData | string
  // @ts-expect-error - TS2749 - 'IX2ActionItemTarget' refers to a value, but is being used as a type here. Did you mean 'typeof IX2ActionItemTarget'?
): IX2ActionItemTarget => {
  if (
    typeof targetConfig.has === 'function' &&
    /* @ts-expect-error Part of IX2 swap work, added in
     * https://github.com/webflow/webflow/pull/37941/ */
    targetConfig.has('nodeId') &&
    /* @ts-expect-error Part of IX2 swap work, added in
     * https://github.com/webflow/webflow/pull/37941/ */
    targetConfig.has('appliesTo')
  ) {
    return targetConfig;
  }

  if (targetConfig == null || typeof targetConfig === 'string') {
    targetConfig = {
      nodeId: targetConfig,
      appliesTo: ACTION_ITEM_TARGET_TYPES.ELEMENT,
    };
  }

  const {
    nodeId,

    appliesTo,

    styleBlockIds,

    useEventTarget,

    boundaryMode,
  } = targetConfig;

  return IX2ActionItemTarget({
    nodeId,
    appliesTo,
    styleBlockIds:
      styleBlockIds &&
      (Array.isArray(styleBlockIds) ? List(styleBlockIds) : styleBlockIds),
    useEventTarget,
    boundaryMode,
  });
};

export const actionItemTargetMatchesElementId = (
  actionItemTarget: IX2ActionItemTargetData,
  state: IX2StoreStateType
) => {
  if (!actionItemTarget) {
    return () => false;
  }

  return (elementId: string | null) => {
    return (
      elementId != null &&
      getNodeIdFromIx2Id(getActionItemTargetNodeId(actionItemTarget, state)) ===
        elementId
    );
  };
};

export const createActionTargetFromHash = (target: string) => {
  const [, appliesTo, nodeId, useEventTrigger] = target.split(
    ACTION_TARGET_HASH_DELIMITER
  );
  return {
    appliesTo,
    nodeId,
    useEventTrigger,
  };
};

// @ts-expect-error - TS2749 - 'IX2ActionItemTarget' refers to a value, but is being used as a type here. Did you mean 'typeof IX2ActionItemTarget'?
export const getActionTargetHash = (target: IX2ActionItemTarget) => {
  const {appliesTo, nodeId, useEventTrigger} = target;
  return [ACTION_TARGET_HASH_PREFIX, appliesTo, nodeId, useEventTrigger].join(
    ACTION_TARGET_HASH_DELIMITER
  );
};

const stripId = (thing: any) => thing.set('id', '');
const stripCreatedOn = (thing: any) => thing.set('createdOn', 0);
const stripIds = map(stripId);

const stripTargetNodeId = (target: any) =>
  target &&
  (target.appliesTo === 'ELEMENT_CLASS' ||
    target.appliesTo === 'TRIGGER_ELEMENT')
    ? target.set('nodeId', '')
    : target;

const stripParamGroupIds = (actionList: any) =>
  actionList.continuousParameterGroups != null
    ? actionList.update('continuousParameterGroups', stripIds)
    : actionList;

const stripActionItemsIds = map((item) =>
  stripId(item).updateIn(CONFIG_TARGET_PATH, stripTargetNodeId)
);

const prepareActionListForComparing = pipe([
  stripId,
  (actionList: any) => actionList.update('title', getPrefix),
  stripCreatedOn,
  updateActionItemsInList(stripActionItemsIds),
  stripParamGroupIds,
]);

const actionListsMatch = (first: any) => {
  const strippedFirst = prepareActionListForComparing(first);
  return (second: any) =>
    // @ts-expect-error - TS2339: Property 'equals' does not exist on type 'IX2ActionListType'.
    strippedFirst.equals(prepareActionListForComparing(second));
};

type IdStrategy = {
  nextActionListId: () => string;
  nextEventId: () => string;
  nextInteractionId: () => string;
  nextActionListName: (title: string) => string;
};

const importActionList =
  (incomingActionList: any, idStrategy?: IdStrategy) =>
  // @ts-expect-error - TS7031 - Binding element 'actionLists' implicitly has an 'any' type. | TS7031 - Binding element 'renames' implicitly has an 'any' type. | TS7031 - Binding element 'actionListIdMap' implicitly has an 'any' type.
  ({actionLists, renames, actionListIdMap}) => {
    const actionList = duplicateActionList(
      incomingActionList,
      actionLists,
      '',
      idStrategy
    );
    const oldTitle = incomingActionList.title;
    // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'RecordOf<IX2ActionList>'.
    const newTitle = actionList.title;
    const oldId = incomingActionList.id;
    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'RecordOf<IX2ActionList>'.
    const newId = actionList.id;

    return {
      actionLists: actionLists.push(actionList),
      renames:
        newTitle === oldTitle
          ? renames
          : renames.concat([{from: oldTitle, to: newTitle}]),
      actionListIdMap: assoc(oldId)(newId)(actionListIdMap),
    };
  };

/**
 * Creates a function that returns sequential unique IDs.
 * Delegates the initial O(N) scan to the provided seed function
 * (e.g. getUniqueEventId), then increments in O(1).
 */
const createIdGenerator = (seedFn: () => string): (() => string) => {
  let prefix: string | undefined;
  let counter: number;

  return () => {
    if (prefix === undefined) {
      const id = seedFn();

      const match = id.match(/^(.+)-(\d+)$/);
      prefix = match ? match[1]! : id;
      counter = match ? parseInt(match[2]!, 10) : 1;

      return id;
    }

    return `${prefix}-${++counter}`;
  };
};

/**
 * Creates a function that returns unique titles by maintaining
 * an incrementally-updated Set. Defers the O(N) scan to the first
 * call, then resolves each title in O(1) amortized.
 */
const createActionListNameGenerator = (
  actionLists: IX2ActionList
): ((incomingTitle: string) => string) => {
  let slugSet: Set<string> | undefined;

  return (incomingTitle: string): string => {
    if (slugSet === undefined) {
      slugSet = new Set<string>();
      actionLists.forEach((item) => {
        const title = item.title || '';
        if (title) slugSet!.add(slugify(title));
      });
    }

    const slugged = slugify(incomingTitle);
    if (!slugSet.has(slugged)) {
      slugSet.add(slugged);
      return incomingTitle;
    }
    let suffix = 2;
    while (slugSet.has(slugify(`${incomingTitle} ${suffix}`))) {
      suffix++;
    }
    const newTitle = `${incomingTitle} ${suffix}`;
    slugSet.add(slugify(newTitle));
    return newTitle;
  };
};

export const importXscpActionLists = (
  existingLists: IX2ActionList,
  idStrategy?: IdStrategy
) =>
  reduce((result, incomingActionList) =>
    maybe(
      // If we did not find a matching action list we need to import
      importActionList(incomingActionList, idStrategy)
    )(
      // If we found a matching action list we need to update the ID map for
      // events to point to the correct action list.
      (foundList) =>
        ({actionLists, renames, actionListIdMap}) => ({
          actionLists,
          renames,
          // @ts-expect-error - TS18046 - 'incomingActionList' is of type 'unknown'. | TS18046 - 'foundList' is of type 'unknown'.
          actionListIdMap: assoc(incomingActionList.id)(foundList.id)(
            actionListIdMap
          ),
        })
    )(
      // If id strategy is provided (batch import) and the action list has an origin
      // it means we are importing a library component and we don't need to check for duplicates
      // @ts-expect-error - TS18046 - 'incomingActionList' is of type 'unknown'.
      idStrategy && incomingActionList.origin
        ? None
        : // @ts-expect-error - TS18046 - 'result' is of type 'unknown'.
          find(actionListsMatch(incomingActionList))(result.actionLists)
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type '{ actionLists: any; renames: any; actionListIdMap: any; }'.
    )(result)
  )({
    actionLists: existingLists,
    renames: emptyArray,
    actionListIdMap: emptyObject,
  });

// @ts-expect-error - TS7031 - Binding element 'events' implicitly has an 'any' type. | TS7031 - Binding element 'eventIdMap' implicitly has an 'any' type.
const replaceAutoStopEventIds = ({events, eventIdMap}) => ({
  // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
  events: events.map((event) =>
    event.hasIn(EVENT_AUTO_STOP_ID_KEY_PATH)
      ? event.updateIn(
          EVENT_AUTO_STOP_ID_KEY_PATH,
          // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
          (id) => eventIdMap[id] || id
        )
      : event
  ),
  eventIdMap,
});

const stripAutoStopEventId = (event: any) =>
  event.hasIn(EVENT_AUTO_STOP_ID_KEY_PATH)
    ? event.setIn(EVENT_AUTO_STOP_ID_KEY_PATH, '')
    : event;

const stripEventTargetId = (target: any) =>
  target && target.appliesTo === 'CLASS' ? target.set('id', '') : target;

const prepareEventForComparing = pipe([
  stripId,
  stripAutoStopEventId,
  stripCreatedOn,
  (event: any) => {
    if (event.has('target')) {
      event = event.update('target', stripEventTargetId);
    }

    if (event.has('targets')) {
      // @ts-expect-error - TS7006 - Parameter 'targets' implicitly has an 'any' type.
      event = event.update('targets', (targets) =>
        targets.map(stripEventTargetId)
      );
    }

    return event;
  },
  // mediaQueries is a plain array and would fail the Immutable equals
  (event: any) => event.update('mediaQueries', List),
]);

const eventsMatch = (first: any) => {
  const strippedFirst = prepareEventForComparing(first);
  return (second: any) =>
    strippedFirst.equals(prepareEventForComparing(second));
};

export const replaceActionListIdInEvent =
  // @ts-expect-error - TS7031 - Binding element 'actionLists' implicitly has an 'any' type. | TS7031 - Binding element 'actionListIdMap' implicitly has an 'any' type.

  ({actionLists, actionListIdMap}) =>
    (event: any) =>
      event.hasIn(EVENT_ACTIONLIST_ID_KEY_PATH)
        ? event
            // @ts-expect-error - TS7006 - Parameter 'config' implicitly has an 'any' type.
            .update('config', (config) =>
              getConfigurationForEvent(
                event.action,
                actionLists.find(
                  // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
                  ({id}) =>
                    id ===
                    actionListIdMap[event.getIn(EVENT_ACTIONLIST_ID_KEY_PATH)]
                ),
                null,
                config
              )
            )
            .updateIn(
              EVENT_ACTIONLIST_ID_KEY_PATH,
              // @ts-expect-error - TS7006 - Parameter 'actionListId' implicitly has an 'any' type.
              (actionListId) => actionListIdMap[actionListId] || actionListId
            )
        : event;

const importEvent =
  (event: any, idStrategy?: IdStrategy) =>
  // @ts-expect-error - TS7031 - Binding element 'events' implicitly has an 'any' type. | TS7031 - Binding element 'eventIdMap' implicitly has an 'any' type. | TS7031 - Binding element 'forceImport' implicitly has an 'any' type.
  ({events, eventIdMap, forceImport}) => {
    const newId = idStrategy
      ? idStrategy.nextEventId()
      : getUniqueEventId(events);
    return {
      events: events.push(event.set('id', newId)),
      eventIdMap: assoc(event.id)(newId)(eventIdMap),
      forceImport: event.hasIn(EVENT_AUTO_STOP_ID_KEY_PATH)
        ? assoc(event.getIn(EVENT_AUTO_STOP_ID_KEY_PATH))(true)(forceImport)
        : forceImport,
    };
  };

export const importXscpEvents = (
  existingEvents: any,
  idStrategy?: IdStrategy
) =>
  compose(replaceAutoStopEventIds)(
    // @ts-expect-error - TS2345 - Argument of type '(arg2: unknown[]) => unknown' is not assignable to parameter of type '(arg2: unknown[]) => { events: any; eventIdMap: any; }'.
    reduce((result, incomingEvent) =>
      maybe(
        // Did not find matching event, need to import
        importEvent(incomingEvent, idStrategy)
      )(
        // Found a matching event, nothing to do
        constantIdentity
      )(
        // @ts-expect-error - TS18046 - 'result' is of type 'unknown'. | TS18046 - 'incomingEvent' is of type 'unknown'.
        result.forceImport[incomingEvent.id] ||
          // If id strategy is provided (batch import) and the event has an origin
          // it means we are importing a library component and we don't need to check for duplicates
          // @ts-expect-error - TS18046 - 'incomingEvent' is of type 'unknown'.
          (idStrategy && incomingEvent.origin)
          ? None
          : // @ts-expect-error - TS18046 - 'result' is of type 'unknown'.
            find(eventsMatch(incomingEvent))(result.events)
        // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type '{ events: any; eventIdMap: any; forceImport: any; }'.
      )(result)
    )({
      events: existingEvents,
      eventIdMap: emptyObject,
      forceImport: emptyObject,
    })
  );

export const importXscpInteractions = (
  eventIdMap: {[key: string]: any},
  idStrategy?: IdStrategy
) =>
  reduce((interactions, incomingInteraction) =>
    // @ts-expect-error - TS18046 - 'incomingInteraction' is of type 'unknown'. | TS7006 - Parameter 'eventId' implicitly has an 'any' type.
    incomingInteraction.eventIds.every((eventId) => has(eventId)(eventIdMap))
      ? // @ts-expect-error - TS18046 - 'interactions' is of type 'unknown'.
        interactions.push(
          // @ts-expect-error - TS18046 - 'incomingInteraction' is of type 'unknown'.
          incomingInteraction
            .set(
              'id',
              idStrategy
                ? idStrategy.nextInteractionId()
                : // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'IX2Interactions'.
                  getUniqueInteractionId(interactions)
            )
            .update(
              'eventIds',
              // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
              map((eventId) => eventIdMap[eventId] || eventId)
            )
        )
      : interactions
  );

const TARGET_STYLEBLOCKIDS_PATH = ['target', 'styleBlockIds'];
export const replaceStyleBlockIds = (styleBlockIdMap: {
  [key: string]: string;
}) => {
  // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
  const mapStyleBlockId = map((id) => styleBlockIdMap[id] || id);

  return (state: any) =>
    state
      .update(
        'events',
        map((event) => {
          // @ts-expect-error - TS18046 - 'event' is of type 'unknown'.
          event = event.updateIn(TARGET_STYLEBLOCKIDS_PATH, mapStyleBlockId);
          // @ts-expect-error - TS18046 - 'event' is of type 'unknown'.
          if (event.has('targets')) {
            // @ts-expect-error - TS18046 - 'event' is of type 'unknown'. | TS7006 - Parameter 'targets' implicitly has an 'any' type.
            event = event.update('targets', (targets) =>
              // @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
              targets.map((target) =>
                target.update('styleBlockIds', mapStyleBlockId)
              )
            );
          }

          return event;
        })
      )
      .update(
        'actionLists',
        map(
          updateActionItemsInList(
            map((item: any) =>
              // @ts-expect-error - TS18046 - 'item' is of type 'unknown'. | TS7006 - Parameter 'target' implicitly has an 'any' type.
              item.updateIn(CONFIG_TARGET_PATH, (target) =>
                target && target.styleBlockIds
                  ? target.update('styleBlockIds', mapStyleBlockId)
                  : target
              )
            )
          )
        )
      );
};

const replaceStyleVariableIds = (styleVariableIdMap: {
  [key: string]: string;
}) => {
  return (state: IX2StoreStateType) => {
    const mapStyleVariableId = (id: string | undefined) => {
      if (!id) return id;

      const variableId = StyleVariables.getVariableIdFromPlaceholder(id);

      if (variableId) {
        const localVariableId = styleVariableIdMap[variableId];

        if (localVariableId) {
          return StyleVariables.variablePlaceholder(localVariableId);
        }
      }

      return id;
    };

    return state.update(
      'actionLists',
      map(
        updateActionItemsInList(
          map((item: RecordOf<ActionItemType>) => {
            if (item.actionTypeId === ActionTypeConsts.PLUGIN_VARIABLE) {
              return item.updateIn(
                CONFIG_TARGET_PATH,
                (target: RecordOf<ActionItemTargetType>) =>
                  target && target.objectId
                    ? target.update('objectId', mapStyleVariableId)
                    : target
              );
            }
            return item;
          })
        )
      )
    );
  };
};

/**
 * This utility is used when importing interactions
 * from element presets from Library plugins.
 *
 * 1. Imports any interactions from a given an element preset (if any)
 * 2. Replaces ix2 target IDs (elements and StyleBlocks) with new ids from idMap
 * 3. Returns an updated IX2StoreState with new interactions
 */
export const importIx2FromElementPreset = (
  plugins: Plugins.PluginsType,
  elementPresetName: Plugins.QualifiedElementPreset,
  ix2State: IX2StoreStateType,
  idMap: {
    [key: string]: string;
  },
  currentPageId?: string
): IX2StoreStateType => {
  const elementPreset = Plugins.getElementPreset(plugins, elementPresetName);
  if (!elementPreset) {
    return ix2State;
  }

  const interactionsOnElementPreset =
    ElementPreset.getInteractions(elementPreset);
  if (!interactionsOnElementPreset) {
    return ix2State;
  }

  const elementPresetImportStrategy = Plugins.getElementPresetImportStrategy(
    plugins,
    elementPresetName
  );

  const pluginName = getPluginName(elementPresetName);

  return importIx2({
    ix2Data: interactionsOnElementPreset,
    importStrategy: elementPresetImportStrategy,
    pluginName,
    ix2State,
    idMap,
    currentPageId,
  });
};

/**
 * Batch-imports IX2 data from multiple element presets in a single pass.
 * This avoids calling importIx2 per component (which triggers expensive
 * conversions and O(n) scans on the growing state for each one).
 */
export const batchImportIx2FromElementPresets = (
  plugins: Plugins.PluginsType,
  elementPresetNames: Plugins.QualifiedElementPreset[],
  ix2State: IX2StoreStateType,
  idMap: {[key: string]: string}
): IX2StoreStateType => {
  const allIx2Data: {
    interactions: InteractionsType;
    events: EventsType;
    actionLists: ActionListsType;
  } = {interactions: [], events: [], actionLists: []};

  const seenActionListIds = new Set<string>();
  const seenEventIds = new Set<string>();
  const seenInteractionIds = new Set<string>();

  let batchPluginName: PluginName | undefined;
  let batchImportStrategy: Plugins.PluginImportStrategy | undefined;

  for (const elementPresetName of elementPresetNames) {
    const elementPreset = Plugins.getElementPreset(plugins, elementPresetName);
    if (!elementPreset) continue;

    const ix2 = ElementPreset.getInteractions(elementPreset);
    if (!ix2) continue;

    if (!batchPluginName) {
      batchPluginName = getPluginName(elementPresetName);
      batchImportStrategy = Plugins.getElementPresetImportStrategy(
        plugins,
        elementPresetName
      );
    }

    for (const a of ix2.actionLists) {
      if (!seenActionListIds.has(a.id)) {
        seenActionListIds.add(a.id);
        allIx2Data.actionLists.push(a);
      }
    }
    for (const e of ix2.events) {
      if (!seenEventIds.has(e.id)) {
        seenEventIds.add(e.id);
        allIx2Data.events.push(e);
      }
    }
    for (const i of ix2.interactions) {
      if (!seenInteractionIds.has(i.id)) {
        seenInteractionIds.add(i.id);
        allIx2Data.interactions.push(i);
      }
    }
  }

  if (
    !batchPluginName ||
    !batchImportStrategy ||
    (allIx2Data.interactions.length === 0 &&
      allIx2Data.events.length === 0 &&
      allIx2Data.actionLists.length === 0)
  ) {
    return ix2State;
  }

  return importIx2({
    ix2Data: allIx2Data,
    importStrategy: batchImportStrategy,
    pluginName: batchPluginName,
    ix2State,
    idMap,
    batchMode: true,
  });
};

type IX2Data = {
  interactions: InteractionsType;
  events: EventsType;
  actionLists: ActionListsType;
};
type IX2Item = ActionListType | EventType | InteractionType;

/**
 * Internal utility method used to set the `origin` field
 * on the incoming ix2Data to import (events, interactions, actionLists).
 * Additionally, it filters out interactions that already exist in state
 * with the same origin.
 */
const setIx2DataOrigin = ({
  ix2Data,
  ix2State,
  pluginName,
}: {
  ix2Data: IX2Data;
  ix2State: IX2StoreStateType;
  pluginName?: PluginName | null;
}): IX2Data => {
  const getOrigin = (item: IX2Item) =>
    pluginName && item.id ? qualify(pluginName, item.id) : undefined;

  return deduplicateIx2Data({
    incomingIx2Data: ix2Data,
    exitingIx2Data: {
      actionLists: ix2State.actionLists.toJS(),
      events: ix2State.events.toJS(),
      interactions: ix2State.interactions.toJS(),
    },
    getOriginFromIx2Item: getOrigin,
  });
};

/**
 * This utility is used to import interactions.
 *
 * 1. Imports any given interactions (ix2Data)
 * 2. Replaces ix2 target IDs (elements and StyleBlocks) with new ids from idMap
 * 3. Returns an updated IX2StoreState with new interactions
 */
export const importIx2 = ({
  ix2Data,
  importStrategy,
  pluginName,
  ix2State,
  idMap,
  currentPageId,
  batchMode,
}: {
  ix2Data: {
    interactions: InteractionsType;
    events: EventsType;
    actionLists: ActionListsType;
  };
  ix2State: IX2StoreStateType;
  idMap: {
    [key: string]: string;
  };
  currentPageId?: string;
  importStrategy: Plugins.PluginImportStrategy;
  pluginName?: PluginName | null;
  batchMode?: boolean;
}): IX2StoreStateType => {
  // Only when we're importing by value we want to provide
  // `origin` fields for any interaction data.
  const shouldSetOrigin = importStrategy === Plugins.IMPORT_STRATEGY.VALUE;

  const ix2DataToImport = shouldSetOrigin
    ? setIx2DataOrigin({
        ix2Data,
        pluginName,
        ix2State,
      })
    : ix2Data;

  const newInteractionState = IX2StateRecord({
    actionLists: reifyIx2State(ix2DataToImport.actionLists),
    events: reifyIx2State(ix2DataToImport.events),
    interactions: reifyIx2State(ix2DataToImport.interactions),
  });

  const newInteractionStateWithUpdatedStyleBlockIds =
    replaceStyleBlockIds(idMap)(newInteractionState);

  const newInteractionStateWithUpdatedStyleVariableIds =
    replaceStyleVariableIds(idMap)(newInteractionStateWithUpdatedStyleBlockIds);

  const idToIx2TargetMap = Object.keys(idMap).reduce((ix2TargetMap, oldId) => {
    const newId = idMap[oldId];
    if (!newId) {
      return ix2TargetMap;
    }
    return ix2TargetMap.set(
      oldId,
      // When importing by value, we assume we're adding a new component, and
      // thus IDs should not be scoped to the current page:
      !currentPageId || importStrategy === Plugins.IMPORT_STRATEGY.VALUE
        ? newId
        : setNodeIdFromIx2Id(
            currentPageId,
            newId // get the newId as defined in the idMap by retrieving the value of the key (oldId)
          )
    );
  }, Map<string, string>());

  // We want to remap all target ids in the incoming new interaction state data
  const newInteractionStateWithRemappedIds = updateTargetIds(
    (id: string) => idToIx2TargetMap.get(getNodeIdFromIx2Id(id), id)
    // Casting to `any` since all of the utils are expecting POJOs and not immutable records
  )(newInteractionStateWithUpdatedStyleVariableIds) as any;

  // In batch mode, pre-build ID generators once to avoid O(N^2) rescans
  // of the growing lists during large library imports.
  const idStrategy = batchMode
    ? {
        nextActionListId: createIdGenerator(() =>
          getUniqueActionListId(ix2State.actionLists)
        ),
        nextEventId: createIdGenerator(() => getUniqueEventId(ix2State.events)),
        nextInteractionId: createIdGenerator(() =>
          getUniqueInteractionId(ix2State.interactions)
        ),
        nextActionListName: createActionListNameGenerator(ix2State.actionLists),
      }
    : undefined;

  const actionListImportResult = importXscpActionLists(
    ix2State.actionLists,
    idStrategy
  )(newInteractionStateWithRemappedIds.actionLists);

  const {events, eventIdMap} = importXscpEvents(
    ix2State.events,
    idStrategy
  )(
    newInteractionStateWithRemappedIds.events.map(
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type '{ actionLists: any; actionListIdMap: any; }'.
      replaceActionListIdInEvent(actionListImportResult)
    )
  );

  const interactions = importXscpInteractions(
    eventIdMap,
    idStrategy
  )(ix2State.interactions)(
    newInteractionStateWithRemappedIds.interactions
  ) as IX2Interactions;

  return (
    ix2State
      .set(INTERACTIONS, interactions)
      .set(EVENTS, events)
      // @ts-expect-error - TS18046 - 'actionListImportResult' is of type 'unknown'.
      .set(ACTION_LISTS, actionListImportResult.actionLists)
  );
};

export const getPluginType = (
  selectedElement: ExpressionElement | null | undefined
): null | 'lottie' | 'spline' | 'rive' => {
  if (!selectedElement) {
    return null;
  }
  if (isLottie(selectedElement)) {
    return 'lottie';
  }
  if (isSpline(selectedElement)) {
    return 'spline';
  }
  if (isRive(selectedElement)) {
    return 'rive';
  }

  return null;
};

export const preventRelinkingPlugin = (
  state: DesignerStores,
  nativeId: string
): null | Array<'Lottie' | 'Spline' | 'Rive'> => {
  const {IX2UiStore} = state;
  if (!IX2UiStore.relinkMode) return null;

  const root = selectRootElementWithFocusedInstancesExpanded(state);
  if (root == null) return null;

  const elementId = getGuidNullable(nativeId);
  if (!elementId) return null;

  const element = Expressions.getElementById(root, elementId);
  if (element == null) return null;

  // Don't allow users to relink a plugin action item to any element other than
  // the same type element

  const result = new Set<'Lottie' | 'Spline' | 'Rive'>();
  for (const {actionTypeId} of getSelectedActionItems(IX2UiStore)) {
    if (actionTypeId === ActionTypeConsts.PLUGIN_LOTTIE) {
      if (!isLottie(element)) result.add('Lottie');
    }

    if (actionTypeId === ActionTypeConsts.PLUGIN_SPLINE) {
      if (!isSpline(element)) result.add('Spline');
    }

    if (actionTypeId === ActionTypeConsts.PLUGIN_RIVE) {
      if (!isRive(element)) result.add('Rive');
    }
  }

  return result.size > 0 ? Array.from(result) : null;
};

// Sets the initial symbol paths when an animation begins
export const setInitialSymbolPathsForAnimation = (
  state: any,
  uiNodeState: UiNodeStateType
) => {
  return state
    .set(
      INITIAL_FOCUSED_SYMBOL_NATIVE_ID_PATH,
      uiNodeState.focusedSymbolNativeIdPath
    )
    .set(
      INITIAL_SELECTED_NODE_IN_INSTANCE_NATIVE_ID_PATH,
      uiNodeState.selectedNativeIdPath
    );
};

// @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
const getTitle = ({title}) => title;
const avoidTitleConflict = createConflictResolver(slugify);

const getUniqueActionListName = (actionLists: List<IX2List>) =>
  avoidTitleConflict(actionLists.map(getTitle).toJS());

export const duplicateActionList = (
  actionList: IX2ActionList,
  actionLists: List<IX2List>,
  pageId: string = '',
  idStrategy?: IdStrategy
): RecordOf<IX2ActionList> => {
  const updatePageIdFor = replacePageId(pageId);
  const getUniqueItemId =
    (idMap: Map<never, never> | Map<string, string>) => (actionItems: any) =>
      // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
      actionItems.map((item) => {
        // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
        const newItem = item.set('id', idMap.get(item.id));
        return pageId
          ? newItem.updateIn(
              ['config', 'target'],
              // @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
              (target) =>
                target && target.set('nodeId', updatePageIdFor(target.nodeId))
            )
          : newItem;
      });

  const actionListId = idStrategy
    ? idStrategy.nextActionListId()
    : getUniqueActionListId(actionLists);
  const title = idStrategy
    ? // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'IX2ActionList'.
      idStrategy.nextActionListName(actionList.title || '')
    : // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'IX2ActionList'.
      getUniqueActionListName(actionLists)(actionList.title);

  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'. | TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
  let newActionList = actionList.set('id', actionListId).set('title', title);

  // @ts-expect-error - TS2345 - Argument of type 'List<IX2List>' is not assignable to parameter of type 'IX2List'.
  const {idMap: actionItemIdMap} = getActionItemsFromList(newActionList).reduce(
    (accumulator, {id}) => {
      // @ts-expect-error - TS18048 - 'accumulator' is possibly 'undefined'. | TS2345 - Argument of type 'List<unknown>' is not assignable to parameter of type 'List<IX2ItemTypes>'.
      const newId = getUniqueActionItemId(newActionList, accumulator.ids);
      return {
        idMap: accumulator.idMap.set(id, newId),
        ids: accumulator.ids.push({id: newId}),
      };
    },
    {idMap: Map(), ids: List()}
  );
  // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'List<IX2List>'.
  if (newActionList?.actionItemGroups != null) {
    newActionList = newActionList.update(
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
      'actionItemGroups',
      (actionItemGroups) =>
        // @ts-expect-error - TS7006 - Parameter 'actionGroup' implicitly has an 'any' type.
        actionItemGroups.map((actionGroup) =>
          // @ts-expect-error - TS2345 - Argument of type 'Map<unknown, unknown>' is not assignable to parameter of type 'Map<string, string> | Map<never, never>'.
          actionGroup.update('actionItems', getUniqueItemId(actionItemIdMap))
        )
    );
    // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'List<IX2List>'.
  } else if (newActionList?.continuousParameterGroups != null) {
    const {
      idMap: parameterIdMap,
      /* @ts-expect-error Part of IX2 swap work, added in
       * https://github.com/webflow/webflow/pull/37941/ */
      // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'List<IX2List>'.
    } = newActionList.continuousParameterGroups.reduce(
      // @ts-expect-error - TS7006 - Parameter 'accumulator' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type.
      (accumulator, {id}) => {
        const newId = getUniqueParameterGroupId(newActionList, accumulator.ids);
        return {
          idMap: accumulator.idMap.set(id, newId),
          ids: accumulator.ids.push({id: newId}),
        };
      },
      {idMap: Map(), ids: List()}
    );
    newActionList = newActionList.update(
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
      'continuousParameterGroups',
      (continuousParameterGroups) =>
        // @ts-expect-error - TS7006 - Parameter 'parameterGroup' implicitly has an 'any' type.
        continuousParameterGroups.map((parameterGroup) =>
          parameterGroup
            .set('id', parameterIdMap.get(parameterGroup.id))
            // @ts-expect-error - TS7006 - Parameter 'continuousActionGroups' implicitly has an 'any' type.
            .update('continuousActionGroups', (continuousActionGroups) =>
              // @ts-expect-error - TS7006 - Parameter 'actionGroup' implicitly has an 'any' type.
              continuousActionGroups.map((actionGroup) =>
                actionGroup.update(
                  'actionItems',
                  // @ts-expect-error - TS2345 - Argument of type 'Map<unknown, unknown>' is not assignable to parameter of type 'Map<string, string> | Map<never, never>'.
                  getUniqueItemId(actionItemIdMap)
                )
              )
            )
        )
    );
  }

  // @ts-expect-error - TS2322 - Type 'List<IX2List>' is not assignable to type 'RecordOf<IX2ActionList>'.
  return newActionList;
};

const displayError = (
  message: string,
  environments = ['development', 'test']
) => {
  if (
    message &&
    process.env.NODE_ENV &&
    environments.includes(process.env.NODE_ENV)
  ) {
    console.error(message);
  }
};

const logContinuousListError = (
  actionList: IX2ActionListType | null | undefined
) => {
  // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2ActionListType'.
  if (actionList !== null && !(actionList?.continuousParameterGroups != null)) {
    displayError(
      'If event is continuous, the selected action list must be continuous as well'
    );
    return true;
  }
};

const getNewConfigByType = (
  type: string,
  continuousParameterGroupId = null,
  interactionTypeId: any, // @TODO this should be removed, no longer a part of the function
  eventConfig: any
) => {
  if (type === PARAMETER_TYPES.MOUSE_X) {
    return eventConfig
      .find(
        // @ts-expect-error - TS7031 - Binding element 'selectedAxis' implicitly has an 'any' type.
        ({selectedAxis}) => selectedAxis === EventContinuousMouseAxes.X_AXIS
      )
      .set('continuousParameterGroupId', continuousParameterGroupId);
  }
  if (type === PARAMETER_TYPES.MOUSE_Y) {
    return eventConfig
      .find(
        // @ts-expect-error - TS7031 - Binding element 'selectedAxis' implicitly has an 'any' type.
        ({selectedAxis}) => selectedAxis === EventContinuousMouseAxes.Y_AXIS
      )
      .set('continuousParameterGroupId', continuousParameterGroupId);
  }

  return eventConfig
    .first()
    .set('continuousParameterGroupId', continuousParameterGroupId);
};

export const getConfigurationForEvent = (
  action: IX2EventActionType,
  actionList: IX2ActionListType | null | undefined,
  interactionTypeId: any,
  eventConfig: any
): List<RecordOf<IX2ConfigurationTypes>> => {
  // @ts-expect-error - TS2339 - Property 'actionTypeId' does not exist on type '"NAVBAR_OPEN" | "NAVBAR_CLOSE" | "TAB_ACTIVE" | "TAB_INACTIVE" | "SLIDER_ACTIVE" | "SLIDER_INACTIVE" | "DROPDOWN_OPEN" | "DROPDOWN_CLOSE" | "MOUSE_CLICK" | "MOUSE_SECOND_CLICK" | ... 15 more ... | "PAGE_SCROLL"'.
  if (action && action.actionTypeId === IX2ContinuousActionListAction.id) {
    if (!logContinuousListError(actionList)) {
      if (
        actionList !== null &&
        /* @ts-expect-error Part of IX2 swap work, added in
         * https://github.com/webflow/webflow/pull/37941/ */
        // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2ActionListType'.
        actionList?.continuousParameterGroups != null &&
        /* @ts-expect-error Part of IX2 swap work, added in
         * https://github.com/webflow/webflow/pull/37941/ */
        // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2ActionListType'.
        actionList.continuousParameterGroups.size > 0
      ) {
        return List(
          /* @ts-expect-error Part of IX2 swap work, added in
           * https://github.com/webflow/webflow/pull/37941/ */
          // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2ActionListType'. | TS7006 - Parameter 'group' implicitly has an 'any' type.
          actionList.continuousParameterGroups.map((group) =>
            getNewConfigByType(
              group.type,
              group.id,
              interactionTypeId,
              eventConfig
            )
          )
        );
      }
    }
  }

  return eventConfig;
};

export const isStyleBlockUsedByIx2 =
  (ix2State: any) => (styleBlockId: string) =>
    // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
    ix2State.events.some((event) =>
      event.getIn(['target', 'styleBlockIds'], List()).includes(styleBlockId)
    );

export const keyframeSort = (
  groupA: typeof IX2ContinuousActionGroup | typeof IX2TimedActionGroup,
  groupB: typeof IX2ContinuousActionGroup | typeof IX2TimedActionGroup
) => {
  // @ts-expect-error - TS2339 - Property 'keyframe' does not exist on type 'Factory<IX2ActionItemGroupType> | Factory<IX2ContinuousActionGroupType>'. | TS2339 - Property 'keyframe' does not exist on type 'Factory<IX2ActionItemGroupType> | Factory<IX2ContinuousActionGroupType>'.
  if (groupA?.keyframe != null && groupB?.keyframe != null) {
    // @ts-expect-error - TS2339 - Property 'keyframe' does not exist on type 'Factory<IX2ActionItemGroupType> | Factory<IX2ContinuousActionGroupType>'. | TS2339 - Property 'keyframe' does not exist on type 'Factory<IX2ActionItemGroupType> | Factory<IX2ContinuousActionGroupType>'.
    return groupA.keyframe - groupB.keyframe;
  }
  return 0; // Avoid sorting lists other than continuous
};

export const initializeNodeIdMap = ({
  actionLists = List([]),
  interactions = List([]),
}: IX2StoreState) => {
  let nodeIdMap: IX2StoreState['nodeIdMap'] = Map();

  actionLists.forEach((list) => {
    const allActionItems = getActionItemsFromList(list);
    nodeIdMap = updateNodeIdMapFromActionItems(
      allActionItems,
      nodeIdMap,
      incrementNodeIdInstance
    );
  });

  interactions.forEach(({target, interactionTypeId}) => {
    if (InteractionPages.some((type) => type.id === interactionTypeId)) {
      return;
    }
    nodeIdMap = incrementNodeIdInstance(nodeIdMap, target);
  });

  return nodeIdMap;
};

export const updateNodeIdMapFromActionItems = (
  actionItems: any,
  nodeIdMap: Map<string, any>,
  fn: any
) => {
  // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
  actionItems.forEach((item) => {
    const target = item.getIn(['config', 'target']);
    if (target) {
      nodeIdMap = fn(nodeIdMap, target);
    }
  });

  return nodeIdMap;
};

export const incrementNodeIdInstance = (
  nodeIdMap: Map<string, number>,
  nodeIdOrActionTarget: string | RecordOf<IX2ActionItemTargetData>
): Map<string, number> => {
  if (typeof nodeIdOrActionTarget === 'string') {
    const id = nodeIdOrActionTarget as string;
    if (hasPageId(id)) {
      return nodeIdMap.has(id)
        ? nodeIdMap.update(id, (instances) => instances + 1)
        : nodeIdMap.set(id, 1);
    }
  } else {
    const actionTarget = nodeIdOrActionTarget;
    if (actionTarget && actionTarget.nodeId) {
      return incrementNodeIdInstance(nodeIdMap, actionTarget.nodeId);
    }
  }
  return nodeIdMap;
};

export const decrementNodeIdInstance = (
  nodeIdMap: Map<string, number>,
  nodeIdOrActionTarget: string | RecordOf<IX2ActionItemTargetData>
): Map<string, number> => {
  if (typeof nodeIdOrActionTarget === 'string') {
    const id = nodeIdOrActionTarget as string;
    if (hasPageId(id) && nodeIdMap.has(id)) {
      return nodeIdMap.get(id) === 1
        ? nodeIdMap.delete(id)
        : nodeIdMap.update(id, (instances) => instances - 1);
    }
  } else {
    const actionTarget = nodeIdOrActionTarget;
    if (actionTarget && actionTarget.nodeId) {
      return decrementNodeIdInstance(nodeIdMap, actionTarget.nodeId);
    }
  }
  return nodeIdMap;
};

/**
 * Get ix2 data needed for a component we're detaching
 * and returns an updated IX2State with copied interactions
 * with different id and with no origin.
 * TODO: This should be refactored & moved away to a more appropriate place,
 * as it's also used in use cases outside of libraries.
 * ticket: https://webflow.atlassian.net/browse/STRUCT-925
 */
export function importIx2FromDetachedComponent({
  componentName,
  componentMap,
  ix2State,
  ix2Data,
  styleBlockIds,
  idMap,
  currentPageId,
}: {
  componentName: Qualified<Component.ComponentName>;
  componentMap: Component.ComponentMap;
  ix2State: IX2StoreStateType;
  ix2Data: IX2Data;
  styleBlockIds: string[];
  idMap: {[id: string]: string};
  currentPageId?: string;
}): IX2StoreStateType {
  if (isIx2DataEmpty(ix2Data)) {
    // Early return if there's no ix2 data available
    return ix2State;
  }

  const component = componentMap.get(componentName);
  if (!component) return ix2State;
  const componentRender = Component.getRender(component);
  const ix2DataOnComponent = getInteractionsFromComponentRender(
    componentRender,
    ix2Data,
    styleBlockIds
  );
  if (isIx2DataEmpty(ix2DataOnComponent)) {
    return ix2State;
  }

  return importIx2({
    ix2Data: ix2DataOnComponent,
    ix2State,
    idMap,
    currentPageId,
    importStrategy: Plugins.IMPORT_STRATEGY.VALUE,
    // We set the `pluginName` to null so that ix2 data
    // is imported detached from the origin plugin (with origin: undefined)
    pluginName: null,
  });
}

const isIx2DataEmpty = (ix2Data: IX2Data): boolean =>
  ix2Data.events.length === 0 &&
  ix2Data.interactions.length === 0 &&
  ix2Data.actionLists.length === 0;
